// Writing mechanics from the brand book (Voice · Writing mechanics):
// money in ₹ with Indian grouping, UI dates relative within a week, 12-hour lowercase time.

const DAY_MS = 86_400_000;

export type DueTone = 'late' | 'today' | 'soon' | 'normal';

export interface DueLabel {
  label: string;
  tone: DueTone;
  days: number;
}

/** Parses a YYYY-MM-DD calendar date as local midnight. */
export function parseDay(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);
}

/** Working days after `from` up to and including `to`, counting Monday–Saturday. */
export function workingDaysBetween(from: Date, to: Date): number {
  const start = startOfDay(from);
  const end = startOfDay(to);
  if (end <= start) return 0;
  let count = 0;
  for (let cursor = new Date(start); cursor < end;) {
    cursor.setDate(cursor.getDate() + 1);
    if (cursor.getDay() !== 0) count += 1;
  }
  return count;
}

export function formatINR(value: number): string {
  const hasPaise = Math.round(value * 100) % 100 !== 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: hasPaise ? 2 : 0,
  }).format(value);
}

/** "Fri 24 Oct" within the week ahead, "24 Oct" otherwise; the year only when it isn't the current one. */
export function formatDate(value: string, now = new Date()): string {
  const date = parseDay(value);
  const days = calendarDaysBetween(now, date);
  const sameYear = date.getFullYear() === now.getFullYear();
  const dayMonth = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  if (!sameYear) return `${dayMonth} ${date.getFullYear()}`;
  if (days >= 0 && days < 7) {
    return `${date.toLocaleDateString('en-IN', { weekday: 'short' })} ${dayMonth}`;
  }
  return dayMonth;
}

/** Due-ness is computed, never stored: "5d late" · "Today" · "Tomorrow" · "Fri 24 Oct". */
export function formatDue(value: string, now = new Date()): DueLabel {
  const days = calendarDaysBetween(now, parseDay(value));
  if (days < 0) return { label: `${-days}d late`, tone: 'late', days };
  if (days === 0) return { label: 'Today', tone: 'today', days };
  if (days === 1) return { label: 'Tomorrow', tone: 'soon', days };
  return { label: formatDate(value, now), tone: 'normal', days };
}

/** "11:30 am" */
export function formatTime(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const suffix = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return minutes ? `${hour12}:${String(minutes).padStart(2, '0')} ${suffix}` : `${hour12} ${suffix}`;
}

/** "Today, 11:30 am" · "Yesterday, 6 pm" · "Fri 24 Oct, 9 am" */
export function formatDateTime(value: string, now = new Date()): string {
  const date = new Date(value);
  const days = calendarDaysBetween(date, now);
  const time = formatTime(date);
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Yesterday, ${time}`;
  const sameYear = date.getFullYear() === now.getFullYear();
  const day = date.toLocaleDateString('en-IN', {
    weekday: days < 7 ? 'short' : undefined,
    day: 'numeric',
    month: 'short',
    year: sameYear ? undefined : 'numeric',
  }).replace(',', '');
  return `${day}, ${time}`;
}

/** Compact elapsed time: "just now" · "40 min" · "3 h" · "2 days". */
export function formatDuration(ms: number): string {
  const minutes = Math.floor(Math.max(ms, 0) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

export function formatSince(value: string, now = new Date()): string {
  const label = formatDuration(now.getTime() - new Date(value).getTime());
  return label === 'just now' ? label : `${label} ago`;
}

/** Today as YYYY-MM-DD in local time. */
export function todayISO(now = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
